import { ChevronDown, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import './CrawlJobsGroup.css';

export type CrawlJobsGroupProps = {
  id: string;
  title: string;
  count: number;
  icon: LucideIcon;
  isExpanded: boolean;
  onToggle: () => void;
  expandLabel: string;
  collapseLabel: string;
  emptyMessage: string;
  accent?: 'mapping' | 'attention' | 'progress' | 'completed' | 'failed';
  iconFilled?: boolean;
  className?: string;
  children: ReactNode;
};

export function CrawlJobsGroup({
  id,
  title,
  count,
  icon: Icon,
  isExpanded,
  onToggle,
  expandLabel,
  collapseLabel,
  emptyMessage,
  accent,
  iconFilled = false,
  className,
  children,
}: CrawlJobsGroupProps) {
  const panelId = `knowledge-database-crawl-group-${id}`;

  return (
    <section
      className={[
        'knowledge-database__crawl-group',
        accent && `knowledge-database__crawl-group--${accent}`,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="knowledge-database__crawl-group-toggle"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        aria-label={isExpanded ? collapseLabel : expandLabel}
        onClick={onToggle}
      >
        <span className="knowledge-database__crawl-group-heading">
          <Icon
            className="knowledge-database__crawl-group-icon"
            strokeWidth={2}
            fill={iconFilled ? 'currentColor' : 'none'}
            aria-hidden
          />
          <span className="knowledge-database__crawl-group-title">{title}</span>
          <span className="knowledge-database__crawl-group-count" aria-hidden>
            {count}
          </span>
        </span>
        <ChevronDown
          className={[
            'knowledge-database__crawl-group-chevron',
            isExpanded && 'knowledge-database__crawl-group-chevron--expanded',
          ]
            .filter(Boolean)
            .join(' ')}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {isExpanded && (
        <div id={panelId} className="knowledge-database__crawl-group-panel">
          {count === 0 ? (
            <p className="knowledge-database__crawl-group-empty">
              {emptyMessage}
            </p>
          ) : (
            children
          )}
        </div>
      )}
    </section>
  );
}
